import WorkshopDocumentsPanel from '../components/accounting/WorkshopDocumentsPanel';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ConfirmPaymentDialog from '../components/ConfirmPaymentDialog.js';
import axios from 'axios';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Car, User, Phone, Calendar, Wrench, CheckCircle, FileText, Upload, Printer, Receipt, Clock, Trash2, Camera, X, Scan, Plus, ChevronDown, ChevronUp, Edit2, Save, XCircle, MessageCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast.js';
import GuidanceStepper from '../components/GuidanceStepper.js';
import { vehicleAPI, customerAPI, visitAPI, serviceAPI, partAPI, supplierAPI } from '../services/api.js';
import VisitDeleteConfirmDialog from '../components/VisitDeleteConfirmDialog.js';
import WhatsAppPreviewDialog from '../components/WhatsAppPreviewDialog.js';
import { AuthenticatedFileImage } from '../components/AuthenticatedFileImage.js';
import QuickPrintDialog from '../components/QuickPrintDialog.js';
import { ArchiveEditBanner, PreviewOverlay, ScannerModal, VehicleDetailsHeader, VehicleDetailsStyles, VehiclePrintMenu, VehiclePrintVisitPicker, } from '../components/vehicle-details/VehicleDetailsChrome.js';
import { VehicleCustomerInfoCards } from '../components/vehicle-details/VehicleCustomerInfoCards.js';
import { VehicleDetailsBlockContent } from '../components/vehicle-details/VehicleDetailsBlocks.js';
import { statusSteps, getStatusLabel, getStatusColor } from '../mock/data.js';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../utils/formatters.js';
import { OPERATION_TYPE_LABELS, SOURCE_LABELS, labelFromMap, resolveVisitDisplay } from '../utils/displayLabels.js';
// Drag & drop (Liquid layout)
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useVehicleDetailsLayout } from '../components/vehicle-details/useVehicleDetailsLayout.js';
import { resolveBackendBase } from '../utils/backendBase.js';
import { generateIdempotencyKey } from '../utils/idempotency.js';
import { getStoredToken } from '../utils/authToken.js';
import VehicleVisitCard from '../components/vehicle-details/VehicleVisitCard.js';
import VehicleInvoiceIssueConfirmDialog from '../components/vehicle-details/VehicleInvoiceIssueConfirmDialog.js';
import VehicleVisitStartDialog from '../components/vehicle-details/VehicleVisitStartDialog.js';
import { SortableBlock } from '../components/vehicle-details/VehicleDetailsLayout.js';
import { appendArchiveAudit, normalizePartyCatalog } from '../components/vehicle-details/vehicleDetailsUtils.js';
import { buildVisitPayload as buildVehicleVisitPayload } from '../components/vehicle-details/vehicleDetailsPrint.js';
import { useTechnicianCatalog } from '../components/vehicle-details/useTechnicianCatalog.js';
import { readVehicleList, withVehicleReadTimeout } from '../utils/vehicleRead.js';

// Updated icons imports
const API_URL = ("production" === 'production'
    ? '/api'
    : `${resolveBackendBase()}/api`.replace('//api', '/api'));
const FILE_BASE = "production" === 'production' ? '' : (resolveBackendBase() || '');
const VehicleDetails = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { toast } = useToast();
    const isArchiveSource = useMemo(() => {
        const params = new URLSearchParams(location.search || '');
        return params.get('source') === 'archive' || params.get('editMode') === 'full';
    }, [location.search]);
    const DEFAULT_BLOCKS = useMemo(() => [
        'vehicle_info',
        'visits',
        'status_actions',
    ], []);
    const session = useMemo(() => {
        try {
            const raw = localStorage.getItem('session');
            return raw ? JSON.parse(raw) : null;
        }
        catch (e) {
            return null;
        }
    }, []);
    const guidanceEnabled = session?.guidanceEnabled !== false;
    const userId = session?.id || session?.name || 'default';
    const { sensors, layoutBlocks, layoutLoaded, handleLayoutDragEnd } = useVehicleDetailsLayout({
        userId,
        defaultBlocks: DEFAULT_BLOCKS,
    });
    const [vehicle, setVehicle] = useState(null);
    const [approvals, setApprovals] = useState([]);
    const [showFiles, setShowFiles] = useState(false);
    const [showApprovals, setShowApprovals] = useState(false);
    const { rows: technicians, status: techniciansReadStatus, message: techniciansMessage } = useTechnicianCatalog(id);
    const loadGeneration = useRef(0);
    const [printDialogConfig, setPrintDialogConfig] = useState(null);
    const [printMenuOpen, setPrintMenuOpen] = useState(false);
    const [printVisitPickerOpen, setPrintVisitPickerOpen] = useState(false);
    const [pendingHeaderPrintType, setPendingHeaderPrintType] = useState('');
    const [loadError, setLoadError] = useState('');
    const [invoiceIssueVisit, setInvoiceIssueVisit] = useState(null);
    const [invoiceIssueLoading, setInvoiceIssueLoading] = useState(false);
    const openQuickPrintDialog = async ({ type, visitId, issuedInvoice = null }) => {
        const labelMap = {
            invoice: 'فاتورة مبيعات',
            invoice_print: 'فاتورة مبيعات',
            diagnosis: 'تقرير تشخيص',
            quote: 'عرض سعر',
            receipt: 'طباعة الزيارة',
        };
        const visit = visitId ? visits.find((v) => String(v.id) === String(visitId)) : null;
        if (!visit) {
            setLoadError('اختر زيارة محددة قبل فتح المستند.');
            return;
        }
        if (['invoice', 'invoice_issue', 'invoice_print'].includes(type) && visit.invoiceReadStatus !== 'available' && !issuedInvoice) {
            setLoadError('مصدر الفاتورة غير متاح؛ أعد تحميل الزيارة قبل الإصدار أو الطباعة.');
            return;
        }
        const invoiceNumber = issuedInvoice?.invoice_number
            || issuedInvoice?.invoiceNumber
            || visit.invoiceNumber
            || visit.invoice_number;
        const needsIssuance = type === 'invoice_issue' || (type === 'invoice' && !invoiceNumber);
        if (needsIssuance) {
            if (type === 'invoice_issue' && invoiceNumber) {
                setLoadError('الفاتورة صادرة بالفعل؛ استخدم «طباعة فاتورة صادرة».');
                return;
            }
            setInvoiceIssueVisit(visit);
            return;
        }
        if (type === 'invoice_print' && !invoiceNumber) {
            setLoadError('لا توجد فاتورة صادرة لهذه الزيارة بعد.');
            return;
        }
        const documentType = type === 'invoice_print' || type === 'invoice' ? 'invoice' : type;
        const printableVisit = invoiceNumber ? { ...visit, invoiceNumber, invoice: issuedInvoice || visit.invoice } : visit;
        const nextConfig = {
            key: `${documentType}-${printableVisit?.id || 'vehicle'}-${Date.now()}`,
            title: labelMap[documentType] || 'طباعة مستند',
            phone: vehicle?.customerPhone || vehicle?.ownerPhone || '',
            payloadBuilder: async () => {
                const response = await axios.get(API_URL + '/vehicles/' + vehicle.id + '/visits');
                const rows = Array.isArray(response.data) ? response.data : response.data?.visits;
                const current = rows?.find((row) => String(row.id) === String(visitId));
                if (!current) throw new Error('مصدر الزيارة غير متاح؛ أعد تحميل ملف المركبة.');
                return buildVehicleVisitPayload({ docType: documentType, visit: current, vehicle });
            },
        };
        setPrintDialogConfig(nextConfig);
    };
    const confirmInvoiceIssue = async () => {
        if (!invoiceIssueVisit || invoiceIssueLoading)
            return;
        setInvoiceIssueLoading(true);
        try {
            const response = await visitAPI.issueInvoice(invoiceIssueVisit.id);
            const invoice = response?.data?.invoice || {};
            const visitId = invoiceIssueVisit.id;
            setInvoiceIssueVisit(null);
            openQuickPrintDialog({ type: 'invoice_print', visitId, issuedInvoice: invoice });
            fetchData();
        }
        catch (error) {
            const message = String(error?.response?.data?.detail || 'لا يمكن إصدار فاتورة قبل اعتماد الزيارة.');
            setLoadError(message);
            toast({ title: 'تعذر إصدار الفاتورة', description: message, variant: 'destructive' });
        }
        finally {
            setInvoiceIssueLoading(false);
        }
    };
    const openHeaderPrintDialog = (type, visitId) => {
        setPrintMenuOpen(false);
        setPrintVisitPickerOpen(false);
        openQuickPrintDialog({ type, visitId });
    };
    const handleHeaderPrintPress = (event, type) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        setPendingHeaderPrintType(type);
        setPrintMenuOpen(false);
        setPrintVisitPickerOpen(true);
    };
    const openLatestVisitPrintDialog = (event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        const visit = visits.find(v => (v.status || '').toLowerCase() === 'in_progress') || filteredVisits[0] || visits[0];
        if (!visit)
            return;
        const st = (visit.status || '').toLowerCase();
        const type = st === 'quotation' ? 'quote' : st === 'diagnosis' ? 'diagnosis' : 'invoice';
        openQuickPrintDialog({ type, visitId: visit.id });
    };
    const [visits, setVisits] = useState([]);
    const [servicesCatalog, setServicesCatalog] = useState([]);
    const [partsCatalog, setPartsCatalog] = useState([]);
    const [suppliersCatalog, setSuppliersCatalog] = useState([]);
    const [customersCatalog, setCustomersCatalog] = useState([]);
    const [visitFilter, setVisitFilter] = useState('all');
    const [startVisitDialogOpen, setStartVisitDialogOpen] = useState(false);
    const [startVisitLoading, setStartVisitLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);
    // Page-level WhatsApp notification (persists across filter changes)
    const [pageWhatsappNotification, setPageWhatsappNotification] = useState(null);
    // Status & Notes (Vehicle Level)
    const [status, setStatus] = useState('diagnosis');
    const [notes, setNotes] = useState('');
    const [assignedTech, setAssignedTech] = useState('');
    // Edit Vehicle & Customer State
    const [isEditingVehicle, setIsEditingVehicle] = useState(false);
    const [isEditingCustomer, setIsEditingCustomer] = useState(false);
    const [isVehicleInfoCollapsed, setIsVehicleInfoCollapsed] = useState(true);
    const [isCustomerInfoCollapsed, setIsCustomerInfoCollapsed] = useState(true);
    const [vehicleForm, setVehicleForm] = useState({});
    const [customerForm, setCustomerForm] = useState({});
    const [scannerOpen, setScannerOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [vehicleFiles, setVehicleFiles] = useState([]);
    const [fileType, setFileType] = useState('photo');
    const [capturedImage, setCapturedImage] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmTarget, setConfirmTarget] = useState(null);
    const [deleteVisitOpen, setDeleteVisitOpen] = useState(false);
    const [deleteVisitTarget, setDeleteVisitTarget] = useState(null);
    const [deleteVisitLoading, setDeleteVisitLoading] = useState(false);
    const [waPreviewOpen, setWaPreviewOpen] = useState(false);
    const [waPreview, setWaPreview] = useState(null);
    const workshopId = "finmodule-sync";
    useEffect(() => {
        if (!isArchiveSource)
            return;
        setIsEditingVehicle(true);
        setIsEditingCustomer(true);
        setIsVehicleInfoCollapsed(false);
        setIsCustomerInfoCollapsed(false);
        setVisitFilter('all');
    }, [isArchiveSource]);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const API_URL = ("production" === 'production'
        ? '/api'
        : `${resolveBackendBase()}/api`.replace('//api', '/api'));
    // session/userId now initialized at top of component for layout saving
    const appendService = useCallback((service) => {
        if (!service)
            return;
        setServicesCatalog((prev) => {
            const exists = prev.some((s) => s.id === service.id || (s.name || '').trim() === (service.name || '').trim());
            return exists ? prev : [...prev, service];
        });
    }, []);
    const appendPart = useCallback((part) => {
        if (!part)
            return;
        setPartsCatalog((prev) => {
            const exists = prev.some((p) => p.id === part.id || (p.name || '').trim() === (part.name || '').trim());
            return exists ? prev : [...prev, part];
        });
    }, []);
    const appendSupplier = useCallback((supplier) => {
        if (!supplier)
            return;
        const normalizedSupplier = normalizePartyCatalog([supplier], 'supplier')[0];
        if (!normalizedSupplier)
            return;
        setSuppliersCatalog((prev) => {
            const exists = prev.some((s) => s.id === normalizedSupplier.id ||
                (s.name || '').trim().toLowerCase() === (normalizedSupplier.name || '').trim().toLowerCase());
            return exists ? prev : [...prev, normalizedSupplier];
        });
    }, []);
    const activeVisit = useMemo(() => {
        const activeStatuses = new Set(['in_progress', 'diagnosis', 'quotation', 'approved', 'waiting_approval', 'waiting_for_parts', 'repair', 'quality_check', 'ready', 'delivering']);
        return visits.find((v) => activeStatuses.has(String(v.status || '').toLowerCase())) || null;
    }, [visits]);
    const guidanceSteps = useMemo(() => {
        const vehicleReady = Boolean(vehicle && (vehicle.customerName || vehicle.customerId) && vehicle.plateNumber);
        const hasItems = visits.some((visit) => (visit.items || []).length > 0);
        const statusUpdated = !activeVisit;
        return [
            {
                id: 'vehicle',
                title: 'بيانات العميل والمركبة',
                hint: 'تأكد من الاسم والجوال ولوحة المركبة لتجنب الأخطاء الإملائية.',
                done: vehicleReady,
            },
            {
                id: 'items',
                title: 'تسجيل البنود',
                hint: 'أضف الخدمات أو القطع وتحقق من الأسعار قبل الحفظ.',
                done: hasItems,
            },
            {
                id: 'status',
                title: 'تحديث حالة الزيارة',
                hint: 'غيّر الحالة عند الانتهاء لإغلاق الزيارة وعدم نسيانها.',
                done: statusUpdated,
            },
        ];
    }, [vehicle, visits, activeVisit]);
    const canDeleteVisit = ['manager', 'admin'].includes(session?.role);
    const filteredVisits = useMemo(() => {
        const sorted = [...visits].sort((a, b) => {
            const da = new Date(a.entryDate || a.entry_date || a.createdAt || a.created_at || 0).getTime();
            const db = new Date(b.entryDate || b.entry_date || b.createdAt || b.created_at || 0).getTime();
            return db - da;
        });
        const activeStatuses = new Set(['in_progress', 'diagnosis', 'quotation', 'approved', 'waiting_approval', 'waiting_for_parts', 'repair', 'quality_check', 'ready', 'delivering']);
        if (visitFilter === 'open') {
            return sorted.filter((v) => activeStatuses.has(String(v.status || '').toLowerCase()));
        }
        if (visitFilter === 'closed') {
            return sorted.filter((v) => ['completed', 'cancelled', 'delivered'].includes(String(v.status || '').toLowerCase()));
        }
        return sorted;
    }, [visits, visitFilter]);
    const normalizeListPayload = useCallback((response, preferredKeys = []) => {
        const payload = response?.data;
        if (Array.isArray(payload))
            return payload;
        if (payload && typeof payload === 'object') {
            for (const key of preferredKeys) {
                if (Array.isArray(payload?.[key]))
                    return payload[key];
            }
            if (Array.isArray(payload?.data))
                return payload.data;
            if (Array.isArray(payload?.items))
                return payload.items;
            if (Array.isArray(payload?.results))
                return payload.results;
        }
        return [];
    }, []);
    // Lightweight fetch that doesn't show loading spinner
    const fetchDataLight = useCallback(async () => {
        const generation = loadGeneration.current;
        try {
            const visitsRes = await withVehicleReadTimeout(axios.get(`${API_URL}/vehicles/${id}/visits`));
            const rows = readVehicleList(visitsRes, 'visits');
            if (generation === loadGeneration.current) setVisits(rows);
        }
        catch (e) {
            if (generation === loadGeneration.current) {
                setVehicle(null);
                setLoadError('بيانات الزيارات غير متاحة حاليًا؛ أعد تحميل الصفحة.');
            }
        }
    }, [id, API_URL]);
    // Handler for when a visit is closed - shows WhatsApp notification at page level
    const handleVisitClosed = useCallback((notification) => {
        if (notification) {
            setPageWhatsappNotification(notification);
            // Switch filter to 'all' so user can see the closed visit
            setVisitFilter('all');
        }
        fetchDataLight(); // Refresh visits without full loading
    }, [fetchDataLight]);
    const fetchData = useCallback(async () => {
        const generation = ++loadGeneration.current;
        const isCurrent = () => generation === loadGeneration.current;
        try {
            setLoading(true);
            setLoadingProgress(10);
            // Only required sources gate the page. The technician lookup is independent.
            const [vehicleRes, visitsRes] = await Promise.all([
                withVehicleReadTimeout(vehicleAPI.getById(id)),
                withVehicleReadTimeout(axios.get(`${API_URL}/vehicles/${id}/visits`)),
            ]);
            if (!isCurrent()) return;
            if (!vehicleRes?.data?.id) throw new Error('VEHICLE_SOURCE_UNAVAILABLE');
            const visitRows = readVehicleList(visitsRes, 'visits');
            setLoadError('');
            setVehicle(vehicleRes.data);
            setStatus(vehicleRes.data.status || 'diagnosis');
            setNotes(vehicleRes.data.notes || '');
            setAssignedTech(vehicleRes.data.technicianId || '');
            setVehicleForm({
                plateNumber: vehicleRes.data.plateNumber,
                brand: vehicleRes.data.brand,
                model: vehicleRes.data.model,
                vin: vehicleRes.data.vin,
                color: vehicleRes.data.color,
                fileNumber: vehicleRes.data.fileNumber || ''
            });
            setCustomerForm({
                name: vehicleRes.data.customerName,
                phone: vehicleRes.data.customerPhone,
                email: vehicleRes.data.customerEmail,
                fileNumber: vehicleRes.data.customerFileNumber || ''
            });
            setVisits(visitRows);
            // Show loading state as false for core UI
            setLoadingProgress(85);
            setLoading(false);
            // Load files, approvals and catalogs in background. Financial integrity runs server-side.
            const token = getStoredToken();
            const filesPromise = fetch(`${API_URL}/vehicles/${id}/files`, {
                credentials: 'include',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            }).then((r) => r.ok ? r.json() : ({ files: [] })).catch(() => ({ files: [] }));
            const approvalsPromise = axios.get(`${API_URL}/approvals?vehicle_id=${id}`).catch(() => ({ data: [] }));
            const servicesPromise = serviceAPI.getAll().catch(() => ({ data: [] }));
            const partsPromise = partAPI.getAll().catch(() => ({ data: [] }));
            const suppliersPromise = supplierAPI.getAll(workshopId ? { workshop_id: workshopId } : {}).catch(() => ({ data: [] }));
            const customersPromise = customerAPI.getAll().catch(() => ({ data: [] }));
            const [filesRes, approvalsRes, servicesRes, partsRes, suppliersRes, customersRes] = await Promise.all([
                filesPromise, approvalsPromise, servicesPromise, partsPromise, suppliersPromise, customersPromise,
            ]);
            if (!isCurrent()) return;
            setVehicleFiles(filesRes.files || []);
            setServicesCatalog(normalizeListPayload(servicesRes, ['services']));
            setPartsCatalog(normalizeListPayload(partsRes, ['parts']));
            setSuppliersCatalog(normalizePartyCatalog(normalizeListPayload(suppliersRes, ['suppliers']), 'supplier'));
            setCustomersCatalog(normalizePartyCatalog(normalizeListPayload(customersRes, ['customers']), 'customer'));
            const approvalsRows = normalizeListPayload(approvalsRes, ['approvals']);
            const approvalsByVisit = new Map();
            approvalsRows.forEach((a) => {
                const vId = a.visitId || a.visit_id;
                if (!vId)
                    return;
                if (!approvalsByVisit.has(vId))
                    approvalsByVisit.set(vId, []);
                approvalsByVisit.get(vId).push(a);
            });
            setApprovals(approvalsRows);
        }
        catch (error) {
            if (!isCurrent()) return;
            setVehicle(null);
            setVisits([]);
            setLoadError('بيانات المركبة أو الزيارات غير متاحة حاليًا. أعد تحميل الصفحة للمحاولة.');
        }
        finally {
            if (isCurrent()) {
                setLoading(false);
                setLoadingProgress(100);
            }
        }
    }, [id, API_URL, normalizeListPayload, workshopId]);
    useEffect(() => {
        fetchData();
        return () => { loadGeneration.current += 1; };
    }, [fetchData]);
    // Handle Updates
    const handleUpdateVehicleInfo = async () => {
        try {
            await vehicleAPI.update(id, vehicleForm);
            setVehicle(prev => ({ ...prev, ...vehicleForm }));
            if (isArchiveSource) {
                appendArchiveAudit({
                    action: 'vehicle_update',
                    actionLabel: 'تحديث بيانات المركبة من الأرشيف',
                    vehicleId: id,
                    plateNumber: vehicleForm?.plateNumber || vehicle?.plateNumber || '-',
                    fileNumber: vehicleForm?.fileNumber || vehicle?.fileNumber || '-',
                    details: {
                        brand: vehicleForm?.brand,
                        model: vehicleForm?.model,
                        vin: vehicleForm?.vin,
                    },
                });
            }
            setIsEditingVehicle(false);
            toast({ title: 'تم الحفظ', description: 'تم تحديث بيانات المركبة' });
        }
        catch (e) {
            toast({ title: 'خطأ', description: 'فشل تحديث بيانات المركبة', variant: 'destructive' });
        }
    };
    const handleUpdateCustomerInfo = async () => {
        try {
            // If backend supports updating customer from vehicle update endpoint, use that
            // Or update customer directly. For MVP, we update vehicle record which often holds denormalized data
            // But ideally we update customer entity too.
            await vehicleAPI.update(id, {
                customerName: customerForm.name,
                customerPhone: customerForm.phone,
                customerEmail: customerForm.email,
                customerFileNumber: customerForm.fileNumber || ''
            });
            if (vehicle.customerId) {
                await customerAPI.update(vehicle.customerId, {
                    name: customerForm.name,
                    phone: customerForm.phone,
                    email: customerForm.email,
                    fileNumber: customerForm.fileNumber || ''
                });
            }
            setVehicle(prev => ({
                ...prev,
                customerName: customerForm.name,
                customerPhone: customerForm.phone,
                customerEmail: customerForm.email,
                customerFileNumber: customerForm.fileNumber || ''
            }));
            if (isArchiveSource) {
                appendArchiveAudit({
                    action: 'customer_update',
                    actionLabel: 'تحديث بيانات العميل من الأرشيف',
                    vehicleId: id,
                    plateNumber: vehicle?.plateNumber || vehicleForm?.plateNumber || '-',
                    fileNumber: vehicleForm?.fileNumber || vehicle?.fileNumber || '-',
                    details: {
                        name: customerForm.name,
                        phone: customerForm.phone,
                        customerFileNumber: customerForm.fileNumber || '-',
                    },
                });
            }
            setIsEditingCustomer(false);
            toast({ title: 'تم الحفظ', description: 'تم تحديث بيانات العميل' });
        }
        catch (e) {
            toast({ title: 'خطأ', description: 'فشل تحديث بيانات العميل', variant: 'destructive' });
        }
    };
    const handleArchiveAuditEvent = useCallback((event = {}) => {
        if (!isArchiveSource)
            return;
        appendArchiveAudit({
            action: event.action || 'archive_edit',
            actionLabel: event.actionLabel || 'تعديل من الأرشيف',
            vehicleId: id,
            plateNumber: vehicle?.plateNumber || vehicleForm?.plateNumber || '-',
            fileNumber: vehicleForm?.fileNumber || vehicle?.fileNumber || '-',
            details: {
                visitId: event.visitId,
                status: event.status,
                itemsCount: event.itemsCount,
                totalAmount: event.totalAmount,
            },
        });
    }, [isArchiveSource, id, vehicle?.plateNumber, vehicle?.fileNumber, vehicleForm?.plateNumber, vehicleForm?.fileNumber]);
    const requestDeleteVisit = (visitId) => {
        const v = visits.find((x) => x.id === visitId) || visits.find((x) => x.visitId === visitId);
        setDeleteVisitTarget(v || { id: visitId });
        setDeleteVisitOpen(true);
    };
    const confirmDeleteVisit = async () => {
        if (!deleteVisitTarget?.id)
            return;
        try {
            setDeleteVisitLoading(true);
            await visitAPI.delete(deleteVisitTarget.id);
            handleArchiveAuditEvent({
                action: 'visit_delete',
                actionLabel: 'حذف زيارة من الأرشيف',
                visitId: deleteVisitTarget.id,
            });
            toast({ title: 'تم الحذف', description: 'تم حذف الزيارة بنجاح' });
            setDeleteVisitOpen(false);
            setDeleteVisitTarget(null);
            fetchData();
        }
        catch (e) {
            toast({ title: 'خطأ', description: e?.response?.data?.detail === 'VISIT_HAS_SUPPLIER_REFERENCES' ? 'للزيارة توثيق موردين محفوظ؛ لا يمكن حذفها مع سجلاتها المرتبطة.' : 'فشل حذف الزيارة', variant: 'destructive' });
        }
        finally {
            setDeleteVisitLoading(false);
        }
    };
    // Create new visit handler
    const handleCreateVisit = () => {
        const missingCustomer = !vehicle?.customerName && !vehicle?.customerId;
        if (missingCustomer) {
            toast({
                title: 'تنبيه',
                description: 'يرجى إدخال بيانات العميل والمركبة قبل استقبال الزيارة.',
                variant: 'destructive',
            });
            return;
        }
        const activeStatuses = new Set(['in_progress', 'diagnosis', 'quotation', 'approved', 'waiting_approval', 'waiting_for_parts', 'repair', 'quality_check', 'ready', 'delivering']);
        const hasOpenVisit = visits.some((v) => activeStatuses.has(String(v.status || '').toLowerCase()));
        if (hasOpenVisit) {
            toast({ title: 'زيارة مفتوحة موجودة', description: 'أغلق الزيارة الحالية قبل فتح زيارة جديدة.', variant: 'destructive' });
            return;
        }
        setStartVisitDialogOpen(true);
    };
    const confirmCreateVisit = async ({ mileage }) => {
        if (startVisitLoading)
            return;
        try {
            setStartVisitLoading(true);
            await axios.post(`${API_URL}/vehicles/${id}/visits`, {
                entryDate: new Date().toISOString(),
                status: 'in_progress',
                mileage,
                technicianId: null, // Default none
                notesText: '',
                requestId: generateIdempotencyKey('vehicle-visit-open')
            });
            handleArchiveAuditEvent({
                action: 'visit_create',
                actionLabel: 'إنشاء زيارة جديدة من الأرشيف',
            });
            toast({ title: 'تم', description: 'تم فتح زيارة جديدة' });
            setStartVisitDialogOpen(false);
            fetchData();
        }
        catch (e) {
            toast({ title: 'خطأ', description: 'فشل إنشاء زيارة', variant: 'destructive' });
        }
        finally {
            setStartVisitLoading(false);
        }
    };
    // Handle status update (Vehicle Level)
    const handleStatusUpdate = async () => {
        try {
            await vehicleAPI.update(id, {
                status,
                notes,
                technicianId: assignedTech || null
            });
            if (isArchiveSource) {
                appendArchiveAudit({
                    action: 'vehicle_status_update',
                    actionLabel: 'تحديث حالة المركبة من الأرشيف',
                    vehicleId: id,
                    plateNumber: vehicle?.plateNumber || vehicleForm?.plateNumber || '-',
                    fileNumber: vehicleForm?.fileNumber || vehicle?.fileNumber || '-',
                    details: {
                        status,
                        assignedTech,
                    },
                });
            }
            const wasArchived = ['delivered', 'archived'].includes(String(vehicle?.status || '').toLowerCase());
            if (status === 'delivered') {
                toast({ title: 'تم التسليم والأرشفة', description: 'أُغلقت الزيارة تشغيليًا فقط، ولم يُنشأ أي أثر مالي.' });
                window.dispatchEvent(new CustomEvent('vehicles:updated', { detail: { source: 'vehicle_delivery', vehicleId: id } }));
                window.dispatchEvent(new CustomEvent('archive:updated', { detail: { source: 'vehicle_delivery', vehicleId: id } }));
                navigate('/archive');
                return;
            }
            else if (wasArchived) {
                toast({ title: 'تمت إعادة الملف للعمل', description: 'أُعيد فتح آخر زيارة غير معتمدة تشغيليًا عند توفرها.' });
            }
            else {
                toast({ title: 'تم الحفظ', description: 'تم تحديث حالة المركبة' });
            }
            fetchData();
        }
        catch (e) {
            toast({ title: 'خطأ', description: 'فشل تحديث الحالة', variant: 'destructive' });
        }
    };
    // Scanner Functions
    const openScanner = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                streamRef.current = stream;
            }
            setScannerOpen(true);
        }
        catch (err) {
            toast({ title: 'خطأ', description: 'فشل في فتح الكاميرا', variant: 'destructive' });
        }
    };
    const closeScanner = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        setScannerOpen(false);
        setCapturedImage(null);
    };
    const captureImage = () => {
        if (videoRef.current && canvasRef.current) {
            const canvas = canvasRef.current;
            const video = videoRef.current;
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0);
            canvas.toBlob(async (blob) => {
                const reader = new FileReader();
                reader.onload = (e) => setCapturedImage(e.target.result);
                reader.readAsDataURL(blob);
            }, 'image/jpeg', 0.7);
        }
    };
    const uploadScannedImage = async () => {
        if (!capturedImage)
            return;
        try {
            const blob = await fetch(capturedImage).then(r => r.blob());
            const file = new File([blob], `scan_${Date.now()}.jpg`, { type: 'image/jpeg' });
            const formData = new FormData();
            formData.append('file', file);
            const token = getStoredToken();
            const response = await fetch(`${API_URL}/vehicles/${id}/upload-file?file_type=photo`, { method: 'POST', credentials: 'include', body: formData, headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (response.ok) {
                toast({ title: 'تم الحفظ', description: 'تم حفظ الصورة بنجاح' });
                closeScanner();
                fetchData();
            }
        }
        catch (err) {
            toast({ title: 'خطأ', description: 'فشل في رفع الصورة', variant: 'destructive' });
        }
    };
    const blockTitles = {
        vehicle_info: 'معلومات المركبة',
        visits: 'الزيارات',
        guidance: 'إرشادات الملف',
        status_actions: 'الحالة والإجراءات',
    };
    if (loading) {
        return (_jsxs("div", { className: "flex flex-col items-center justify-center py-20 gap-3", children: [_jsx("div", { className: "w-8 h-8 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin" }), _jsxs("div", { className: "text-xs", style: { color: 'rgba(100,116,139,0.9)' }, "data-testid": "vehicle-loading-status", children: ["\u062C\u0627\u0631\u064A \u0627\u0644\u062A\u062D\u0645\u064A\u0644... ", loadingProgress, "%"] })] }));
    }
    if (!vehicle)
        return (_jsx("div", { className: "text-center py-20", style: { color: 'rgba(100,116,139,0.9)' }, "data-testid": "vehicle-not-found", children: loadError || 'المركبة غير موجودة' }));
    // MARKER: VehicleDetails content begins
    const blockContext = {
        approvals, assignedTech, canDeleteVisit, customerForm, customersCatalog,
fetchData, filteredVisits, guidanceEnabled, guidanceSteps, handleArchiveAuditEvent,
        handleCreateVisit,
        handleStatusUpdate, handleUpdateCustomerInfo, handleUpdateVehicleInfo, handleVisitClosed, id, isArchiveSource,
        isCustomerInfoCollapsed, isEditingCustomer, isEditingVehicle, isVehicleInfoCollapsed, notes, t,
openQuickPrintDialog, openScanner, partsCatalog, requestDeleteVisit, servicesCatalog, setAssignedTech,
        setCustomerForm, setIsCustomerInfoCollapsed, setIsEditingCustomer, setIsEditingVehicle,
        setIsVehicleInfoCollapsed, setNotes, setPreviewImage, setShowFiles, setStatus, setVehicleForm, setVisitFilter,
        setWaPreview, setWaPreviewOpen, showFiles, status, suppliersCatalog, technicians, userId, vehicle, vehicleFiles,
        vehicleForm, visitFilter, visits, techniciansReadStatus, techniciansMessage,
    };
    const renderBlock = (blockId) => (_jsx(VehicleDetailsBlockContent, { blockId: blockId, ctx: blockContext }));
    return (_jsxs("div", { className: "vehicle-details-page mobile-first-vehicle w-full max-w-6xl mx-auto px-0 sm:px-4 pb-28 sm:pb-20 space-y-4 sm:space-y-6", style: { direction: isRTL ? 'rtl' : 'ltr' }, children: [_jsx(VehicleDetailsStyles, { printMenuOpen: printMenuOpen, printVisitPickerOpen: printVisitPickerOpen, printDialogConfig: printDialogConfig }), isArchiveSource && _jsx(ArchiveEditBanner, {}), _jsx(VehicleDetailsHeader, { vehicle: vehicle, isArchiveSource: isArchiveSource, navigate: navigate, printMenuOpen: printMenuOpen, setPrintMenuOpen: setPrintMenuOpen }), _jsx(VehiclePrintMenu, { open: printMenuOpen, onClose: () => setPrintMenuOpen(false), onPrint: handleHeaderPrintPress }), _jsx(VehiclePrintVisitPicker, { open: printVisitPickerOpen, visits: visits, pendingHeaderPrintType: pendingHeaderPrintType, onClose: () => { setPrintVisitPickerOpen(false); setPendingHeaderPrintType(''); }, onPick: openHeaderPrintDialog }), layoutLoaded ? (_jsxs("div", { className: "px-3 sm:px-0", children: [_jsx("div", { className: "hidden md:block text-[11px] mb-2", style: { color: 'rgba(100,116,139,0.9)' }, children: "\u0627\u0633\u062D\u0628 \u0627\u0644\u0628\u0644\u0648\u0643\u0627\u062A \u0645\u0646 \u0632\u0631 (\u22EE\u22EE) \u0644\u062A\u0631\u062A\u064A\u0628 \u0627\u0644\u0635\u0641\u062D\u0629 \u0643\u0645\u0627 \u062A\u0631\u064A\u062F \u2014 \u064A\u062A\u0645 \u0627\u0644\u062D\u0641\u0638 \u062A\u0644\u0642\u0627\u0626\u064A\u0627\u064B." }), _jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleLayoutDragEnd, children: _jsx(SortableContext, { items: layoutBlocks, strategy: verticalListSortingStrategy, children: _jsx("div", { className: "space-y-3", "data-testid": "vehicle-layout", children: layoutBlocks.map((blockId) => (_jsx(SortableBlock, { id: blockId, title: blockTitles[blockId] || blockId, children: renderBlock(blockId) }, blockId))) }) }) })] })) : (_jsx("div", { className: "px-4 sm:px-0 text-xs", style: { color: 'rgba(100,116,139,0.9)' }, children: "\u062C\u0627\u0631\u064A \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u062A\u062E\u0637\u064A\u0637..." })), _jsx(WorkshopDocumentsPanel, { vehicleId: id, title: "مصروفات الورشة المرتبطة بالمركبة" }), pageWhatsappNotification && (_jsx("div", { className: "mx-4 sm:mx-0 mt-4 rounded-2xl px-4 py-3 animate-in fade-in slide-in-from-top-2", style: {
                    background: 'rgba(16,185,129,0.12)',
                    border: '1px solid rgba(16,185,129,0.28)',
                    color: 'rgba(4,120,87,0.95)',
                }, "data-testid": "page-whatsapp-notification", children: _jsxs("div", { className: "flex items-center justify-between gap-3", children: [_jsxs("div", { className: "flex-1", children: [_jsxs("div", { className: "font-bold text-sm flex items-center gap-2 mb-1", style: { color: 'rgba(4,120,87,0.95)' }, children: [_jsx(MessageCircle, { size: 16 }), "\u0625\u0628\u0644\u0627\u063A \u0627\u0644\u0639\u0645\u064A\u0644 \u0628\u062C\u0627\u0647\u0632\u064A\u0629 \u0627\u0644\u0645\u0631\u0643\u0628\u0629"] }), _jsxs("p", { className: "text-xs", style: { color: 'rgba(6,95,70,0.95)' }, children: [pageWhatsappNotification.customerName, " - \u0627\u0636\u063A\u0637 \u0644\u0625\u0631\u0633\u0627\u0644 \u0627\u0644\u0625\u0634\u0639\u0627\u0631 \u0639\u0628\u0631 \u0648\u0627\u062A\u0633\u0627\u0628"] })] }), _jsxs("div", { className: "flex gap-2", children: [_jsxs("a", { href: pageWhatsappNotification.url, target: "_blank", rel: "noopener noreferrer", className: "px-4 py-2 text-xs font-bold rounded-xl transition-colors flex items-center gap-2", style: {
                                        background: 'rgba(16,185,129,0.20)',
                                        border: '1px solid rgba(16,185,129,0.36)',
                                        color: 'rgba(4,120,87,0.95)',
                                    }, "data-testid": "page-whatsapp-send-button", children: [_jsx(MessageCircle, { size: 14 }), " \u0625\u0631\u0633\u0627\u0644 \u0648\u0627\u062A\u0633\u0627\u0628"] }), _jsx("button", { onClick: () => setPageWhatsappNotification(null), className: "px-2 py-2 rounded-xl transition-colors", style: {
                                        background: 'rgba(255,255,255,0.8)',
                                        border: '1px solid rgba(203,213,225,0.8)',
                                        color: 'rgba(100,116,139,0.9)',
                                    }, title: "\u0625\u063A\u0644\u0627\u0642", "data-testid": "page-whatsapp-dismiss-button", children: _jsx(X, { size: 14 }) })] })] }) })), _jsx(ScannerModal, { open: scannerOpen, videoRef: videoRef, canvasRef: canvasRef, capturedImage: capturedImage, setCapturedImage: setCapturedImage, captureImage: captureImage, uploadScannedImage: uploadScannedImage, closeScanner: closeScanner }), _jsx(PreviewOverlay, { previewImage: previewImage, onClose: () => setPreviewImage(null) }), _jsx(VisitDeleteConfirmDialog, { open: deleteVisitOpen, onOpenChange: (v) => {
                    if (deleteVisitLoading)
                        return;
                    setDeleteVisitOpen(v);
                    if (!v)
                        setDeleteVisitTarget(null);
                }, visit: deleteVisitTarget, t: t, isRTL: isRTL, isLoading: deleteVisitLoading, onConfirm: confirmDeleteVisit }), _jsx(WhatsAppPreviewDialog, { open: waPreviewOpen, onOpenChange: setWaPreviewOpen, preview: waPreview, t: t, isRTL: isRTL }), _jsx(VehicleInvoiceIssueConfirmDialog, { open: Boolean(invoiceIssueVisit), onOpenChange: (open) => !invoiceIssueLoading && !open && setInvoiceIssueVisit(null), visit: invoiceIssueVisit, isLoading: invoiceIssueLoading, onConfirm: confirmInvoiceIssue }), _jsx(VehicleVisitStartDialog, { open: startVisitDialogOpen, onOpenChange: setStartVisitDialogOpen, onConfirm: confirmCreateVisit, isLoading: startVisitLoading, defaultMileage: vehicle?.mileage || vehicle?.currentMileage || '' }), _jsx(QuickPrintDialog, { open: Boolean(printDialogConfig), title: printDialogConfig?.title || 'خيارات الطباعة', description: "\u0645\u0639\u0627\u064A\u0646\u0629 \u062A\u0641\u0627\u0635\u064A\u0644 \u0627\u0644\u0632\u064A\u0627\u0631\u0629 \u0642\u0628\u0644 \u0627\u0644\u0637\u0628\u0627\u0639\u0629 \u0623\u0648 \u0627\u0644\u0625\u0631\u0633\u0627\u0644", payloadBuilder: printDialogConfig?.payloadBuilder, initialPhone: printDialogConfig?.phone, onClose: () => setPrintDialogConfig(null) }, printDialogConfig?.key || 'quick-print')] }));
};
export default VehicleDetails;
